import {
  Category,
  CategoryNames,
  SortValues,
  StyleKeywordTopReported,
  Subcategory,
  TechniqueFull,
  TechniqueKeyword,
} from '@omniafishing/core';
import { Button, Divider, Space } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import bgPattern from '../../assets/topo-blue-pattern.png';
import OmniaCarousel from '../../components/carousel/omnia_carousel';
import { ProductFamilyCarousel } from '../../components/carousel/product_family_carousel';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { ContentWrapperBreakout } from '../../components/content_wrapper/content_wrapper_breakout';
import { LinkButton } from '../../components/link_button/link_button';
import Loading from '../../components/loading/loading';
import { OmniaBreadcrumb } from '../../components/omnia_breadcrumb/omnia_breadcrumb';
import { ProductFamilyCard } from '../../components/product_family_card/product_family_card';
import { ProductFamilyLink } from '../../components/product_family_link/product_family_link';
import { ProductLink } from '../../components/product_link/product_link';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SEO } from '../../components/seo/seo';
import SvgExpert from '../../components/svg/expert';
import SvgTackleBox from '../../components/svg/tackle_box';
import { isPending } from '../../constants/loading_state';
import { useCart } from '../../hooks/use_cart';
import { useListAttributionUuid } from '../../hooks/use_list_attribution_uuid';
import { useListViewed } from '../../hooks/use_list_viewed';
import { useQueryString } from '../../hooks/use_query_string';
import { apiV1 } from '../../lib/api';
import { stripHtml } from '../../lib/html_strip';
import { getImgixPath } from '../../lib/imgix';
import { getLineItemCustomAttributes } from '../../lib/line_item_attributes';
import { productFamilyPriceRange } from '../../lib/product_families';
import { OmniaQueryParams } from '../../lib/query_string';
import { setShopifyImgWidth } from '../../lib/set_shopify_img_width';
import { OmniaUrls } from '../../lib/urls';
import { WebAnalytics } from '../../lib/web_analytics';
import { getIpState } from '../../redux/geographic_location';
import {
  fetchRecommendationFamilies,
  getLoadingState,
  getProductFamilies,
} from '../../redux/recommendations';
import {
  getCategories,
  getCategoryBySubcategoryName,
  getSubcategoryBySubcatTypeName,
  getTechniques,
} from '../../redux/reference_data';
import { getIsMobile } from '../../redux/window';
import { RoutePaths } from '../../routes';
import { StyleKeywordFishingReport } from './fishing_report';
import styles from './style_keyword.less';

interface StyleKeywordPageProps {
  styleKeyword: TechniqueKeyword;
  styleKeywords: TechniqueKeyword[];
  style: TechniqueFull;
  category: Category;
  subcategory: Subcategory;
  topReported: StyleKeywordTopReported[];
}
export const StyleKeywordDetail = (props: StyleKeywordPageProps) => {
  const { style, styleKeyword, category, subcategory, styleKeywords, topReported } = props;

  const dispatch = useDispatch();
  const ipState = useSelector(getIpState);
  const categories = useSelector(getCategories);
  const recommendedFamilies = useSelector(getProductFamilies);
  const loadingState = useSelector(getLoadingState);
  const isMobile = useSelector(getIsMobile);
  const allStyles = useSelector(getTechniques);
  const { stringify } = useQueryString();
  const list_uuid = useListAttributionUuid();
  const subcategoryBySubcatTypeName = useSelector(getSubcategoryBySubcatTypeName);
  const categoryBySubcategoryName = useSelector(getCategoryBySubcategoryName);
  const styleSubcategories = _.uniqBy(
    style.subcat_types.map((st) => subcategoryBySubcatTypeName(st)),
    'name'
  );
  const styleCategories = _.uniqBy(
    styleSubcategories
      .map((s) => categoryBySubcategoryName(s.name))
      .concat(
        categories.filter((c) => style.categories.includes(c.name) && c.name !== category.name)
      )
      .filter((c) => c.name !== category.name),
    'name'
  );
  const topReportedFiltered = isMobile ? topReported : topReported.slice(0, 3);
  const { coupon, createListId } = useListViewed();
  const { addToCart } = useCart();

  const [recommendedProductsImagesByCategory, setRecommendedProductsImagesByCategory] = useState(
    {} as Record<CategoryNames, string>
  );

  useEffect(() => {
    styleCategories.forEach((c) => {
      apiV1
        .recommendationsFetch({
          categories: [c.name],
          styles: [style.name],
          page: 1,
          limit: 1,
          sort: SortValues.BESTSELLING,
        })
        .then((res) => {
          setRecommendedProductsImagesByCategory((existing) => ({
            ...existing,
            [c.name]: res.data.data?.[0].product_family.img_url,
          }));
        });
    });
  }, [styleCategories.map((c) => c.name).join()]);

  useEffect(() => {
    dispatch(
      fetchRecommendationFamilies({
        styles: style.name,
        subcategories: subcategory?.name || category.subcategories.map((s) => s.name),
        include_hidden_products: false,
        page: 1,
        limit: 30_000,
        sort: SortValues.BESTSELLING,
      })
    );
  }, []);

  useEffect(() => {
    WebAnalytics.fishingIntentChange({
      techniqueName: style.name,
      stateAbbreviation: ipState,
    });
  }, []);

  const SubcategoryOrCategory = subcategory || category;
  const { description, keyword_text } = styleKeyword;

  const paramsString = stringify({
    [OmniaQueryParams.styles]: [style.name],
    [OmniaQueryParams.sort]: SortValues.BESTSELLING,
  });
  const subcatOrCatUrl = subcategory
    ? `${OmniaUrls.subcategory(category, subcategory)}?${paramsString}`
    : `${OmniaUrls.category(category)}?${paramsString}`;

  const webAnalyticsArgs = {
    product_category: styleKeyword.category,
    product_subcategory: styleKeyword.subcategory,
    technique: style.name,
    keyword_slug: styleKeyword.slug,
  };

  const descriptionText = description
    ? stripHtml(description)
    : `Omnia collects thousands of detailed fishing reports from anglers across the country to power our local recommendations. Here are the top ${SubcategoryOrCategory.display_name} recommendations for using the ${style.display_name} technique mentioned in fishing reports from anglers near you.`;

  useEffect(() => {
    WebAnalytics.productFamilyListViewed({
      productFamilies: topReportedFiltered.map((tr) => tr.product_family),
      coupon,
      list_id: createListId('other'),
      list_uuid,
    });
  }, [topReportedFiltered.map((tr) => tr.product_family.handle)]);

  return (
    <ScrollToTop>
      <section>
        <SEO
          title={keyword_text}
          description={descriptionText}
          ogImage={topReportedFiltered[0]?.product_family.img_url}
        />

        <header
          className={styles.header}
          style={{ backgroundImage: `url(${style.banner_image || bgPattern})` }}
        >
          <div className={styles.headerOverlay}>
            <ContentWrapper>
              <h1>{styleKeyword.keyword_text}</h1>
              {description ? (
                <div dangerouslySetInnerHTML={{ __html: description }} />
              ) : (
                <p>{descriptionText}</p>
              )}
            </ContentWrapper>
          </div>
        </header>

        <section>
          <ContentWrapper>
            <OmniaBreadcrumb
              items={[
                {
                  name: 'Techniques',
                  url: RoutePaths.TECHNIQUES,
                },
                {
                  name: style.display_name,
                  url: OmniaUrls.technique(style),
                },
                {
                  name: category.display_name,
                  url: null,
                },
              ]}
              className={styles.breadcrumb}
            />

            <section>
              <Space direction="vertical" split={<Divider />} style={{ display: 'flex' }}>
                {topReportedFiltered.map((tr) => {
                  const { product_family, products, fishing_reports } = tr;
                  const priceRange = productFamilyPriceRange(product_family);
                  const fishingReportsSliced = isMobile
                    ? fishing_reports
                    : fishing_reports.slice(0, 3);
                  return (
                    <div className={styles.topReported} key={product_family.handle}>
                      <h2 className={styles.productFamilyTitle}>
                        <ProductFamilyLink productFamily={product_family} currentColor>
                          {product_family.title}
                        </ProductFamilyLink>
                      </h2>
                      <p className={styles.priceRange}>{priceRange}</p>

                      <div className={styles.topReportedContent}>
                        <div className={styles.topReportedProductFamily}>
                          <div className={styles.topReportedProductFamilyImg}>
                            <Link
                              to={OmniaUrls.productFamily(product_family.handle)}
                              onClick={() => {
                                WebAnalytics.productClick({
                                  productOrProductFamily: product_family,
                                  show_modal: false,
                                  position: null,
                                });
                              }}
                            >
                              <img src={setShopifyImgWidth(product_family.img_url, 200)} />
                            </Link>
                          </div>
                          <LinkButton
                            to={OmniaUrls.productFamily(product_family.handle)}
                            onClick={() => {
                              WebAnalytics.productClick({
                                productOrProductFamily: product_family,
                                show_modal: false,
                                position: null,
                              });
                            }}
                          >
                            Product Details
                          </LinkButton>
                        </div>

                        <div className={styles.topReportedFishingReports}>
                          {isMobile ? (
                            <ContentWrapperBreakout>
                              <div className={styles.fishingReportsCarousel}>
                                <OmniaCarousel
                                  key={fishingReportsSliced.map((fr) => fr.id).join('')}
                                  arrows={true}
                                >
                                  {fishingReportsSliced.map((fr) => {
                                    return (
                                      <div key={fr.id} className={styles.fishingReportWrapper}>
                                        <StyleKeywordFishingReport
                                          fishing_report={fr}
                                          key={fr.id}
                                          style={style}
                                          techniqueKeyword={styleKeyword}
                                          productFamily={product_family}
                                        />
                                      </div>
                                    );
                                  })}
                                </OmniaCarousel>
                              </div>
                            </ContentWrapperBreakout>
                          ) : (
                            <>
                              {fishingReportsSliced.map((fr) => {
                                return (
                                  <StyleKeywordFishingReport
                                    fishing_report={fr}
                                    key={fr.id}
                                    style={style}
                                    techniqueKeyword={styleKeyword}
                                    productFamily={product_family}
                                  />
                                );
                              })}
                            </>
                          )}
                        </div>

                        <div className={styles.topReportedProducts}>
                          {products.map((p, i) => {
                            return (
                              <div className={styles.topReportedProduct} key={p.sku}>
                                <div className={styles.topReportedProductImg}>
                                  <ProductLink product={p} currentColor>
                                    <img src={setShopifyImgWidth(p.img_url, 100)} />
                                  </ProductLink>
                                </div>
                                <div className={styles.topReportedProductText}>
                                  <p className={styles.topReportedProductPrice}>
                                    <ProductLink product={p} currentColor>
                                      ${p.price}
                                    </ProductLink>
                                  </p>
                                  <p className={styles.topReportedProductOptions}>
                                    <ProductLink product={p} currentColor>
                                      {p.shopify_options.map((so) => so.value).join(' / ')}
                                    </ProductLink>
                                  </p>
                                  <Button
                                    className={classNames(
                                      'btn--shop',
                                      styles.topReportedProductButton
                                    )}
                                    onClick={() => {
                                      addToCart({
                                        productsToAdd: [
                                          {
                                            product: p,
                                            quantity: 1,
                                          },
                                        ],
                                        position: i,
                                        customAttributes: getLineItemCustomAttributes(
                                          location.pathname
                                        ),
                                      });
                                    }}
                                  >
                                    <SvgTackleBox width={16} height={16} /> Add to Cart
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Space>
            </section>
          </ContentWrapper>
        </section>

        <Divider />

        <section>
          <ContentWrapper>
            <h2 className={styles.h3}>
              <strong>Explore More</strong> {SubcategoryOrCategory.display_name}
            </h2>

            <p>
              We curated this list of the most popular {SubcategoryOrCategory.display_name} for
              using {style.display_name}. Browse from these best selling{' '}
              {SubcategoryOrCategory.display_name} products below or explore the full catalog of{' '}
              {SubcategoryOrCategory.display_name} <Link to={subcatOrCatUrl}>here.</Link>
            </p>

            {isPending(loadingState) ? (
              <Loading text="Loading Recommendations" />
            ) : (
              <>
                <div className={styles.carousel}>
                  <ProductFamilyCarousel
                    products_families={recommendedFamilies.map((rf) => rf.product_family)}
                    arrows={true}
                    list_id_title={`Explore More ${SubcategoryOrCategory.display_name}`}
                    list_uuid={list_uuid}
                  >
                    {recommendedFamilies.map((recommendedFamily, i) => {
                      return (
                        <div
                          key={recommendedFamily.product_family.handle}
                          className={styles.carouselProduct}
                        >
                          <ProductFamilyCard
                            recommendedFamily={recommendedFamily}
                            position={i}
                            list_uuid={list_uuid}
                          />
                        </div>
                      );
                    })}
                  </ProductFamilyCarousel>
                </div>
              </>
            )}

            <div className={styles.viewAllButton}>
              <LinkButton
                to={subcatOrCatUrl}
                onClick={() => {
                  WebAnalytics.dynamicSeoPageClick('[products].(view_all)', webAnalyticsArgs);
                }}
              >
                View All {SubcategoryOrCategory.display_name}
              </LinkButton>
            </div>
          </ContentWrapper>
        </section>

        <Divider />

        <section>
          <ContentWrapper>
            <h2 className={styles.h3}>
              <strong>Gear Up</strong> for {style.display_name}
            </h2>
            <p>
              Find the perfect gear to pair with one of our recommended Casting Rods for the{' '}
              {SubcategoryOrCategory.display_name} technique. Explore our full catalog to find the
              perfect gear for your next fishing outing.
            </p>
            <ul className={styles.styleCategories}>
              {styleCategories.map((c) => (
                <li key={c.name} className={styles.styleCategory}>
                  <p>
                    <strong>{c.display_name}</strong> for {style.display_name}
                  </p>

                  <Link
                    to={`${OmniaUrls.category(c)}?${paramsString}`}
                    onClick={() => {
                      WebAnalytics.dynamicSeoPageClick(
                        '[gear_up].(category_link)',
                        webAnalyticsArgs
                      );
                    }}
                  >
                    <img
                      src={getImgixPath(recommendedProductsImagesByCategory[c.name], { w: 150 })}
                      alt={c.img_alt}
                      width={100}
                    />
                    Shop for {c.display_name}
                  </Link>
                </li>
              ))}
            </ul>
          </ContentWrapper>
        </section>

        <section className={styles.expertRecommendationsSection}>
          <ContentWrapper>
            <h2 className={styles.h2}>
              <strong>Get Expert Recommendations</strong> personalized for you
            </h2>
            <p>
              Not sure if {style.display_name} is the right technique for you when fishing on your
              lake? Tell us which species you are trying to catch and where you are planning to fish
              for your next outing and we’ll make a customized, local recommendation based upon the
              most successful techniques reported by anglers near you.
            </p>
            <Link
              to={RoutePaths.GET_STARTED}
              className={styles.getStarted}
              onClick={() => {
                WebAnalytics.dynamicSeoPageClick('[expert].(get_started)', webAnalyticsArgs);
              }}
            >
              Get Started
            </Link>
            <p className={styles.talkExpert}>
              Need hands-on help finding the right products?
              <br />
              <Link
                to={RoutePaths.PERSONAL_SHOPPING}
                onClick={() => {
                  WebAnalytics.dynamicSeoPageClick(
                    '[expert].(personal_shopping)',
                    webAnalyticsArgs
                  );
                }}
              >
                <SvgExpert width={'1.4em'} height={'1.4em'} />
                Talk to an Expert
              </Link>
            </p>
          </ContentWrapper>
        </section>

        {styleKeywords.length > 0 && (
          <>
            <section className={styles.moreTechniques}>
              <ContentWrapper>
                <h2 className={styles.h2}>
                  Explore <strong>More Techniques</strong>
                </h2>
                <ul className={styles.styleKeywordsList}>
                  {styleKeywords.map((sk) => {
                    const thisStyle = allStyles.find((s) => s.name === sk.style);
                    return (
                      <li key={sk.slug} className={styles.styleKeyword}>
                        <Link to={OmniaUrls.techniqueKeyword(thisStyle, sk)}>
                          {sk.keyword_text}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </ContentWrapper>
            </section>

            <Divider />
          </>
        )}

        <section className={styles.aboutOmnia}>
          <ContentWrapper>
            <h3 className={styles.h2}>
              About <strong>Omnia Fishing</strong>
            </h3>
            <p>
              Omnia Fishing helps anglers find the right gear from our massive inventory of 18,000+
              items. We provide personalized tackle recommendations based on the relationships
              between species, style, lake characteristics, season, community fishing reports,
              patterns, and products. We make sure you only see the products that make sense for
              you, on the lakes you fish, at the right time of year.
            </p>

            <p>Omnia is the most personalized tackle shopping on earth!</p>
          </ContentWrapper>
        </section>
      </section>
    </ScrollToTop>
  );
};
